import { IArea, IPartyStageLink, TParticipantRole } from './Party';
import { Spotlight } from './PartyClientRequests';
export type TPrivacyLevel = 'private' | 'friends' | 'public';
// This allows to enforce the types
export type TUserId = 'TUserId';
export type TMessageId = 'TMessageId';
export type TPermissionKey = 'TPermissionId';
export type TPartyKey = 'TPartyKey';
export type TParticipantInviteId = 'TParticipantInviteId';
export type TStageKey = 'main-stage';
export enum EAreaPrefix {
  PartyPrefix = 'party-',
  ChatPrefix = 'chat-',
  LobbyPrefix = 'lobby-',
}
export type TAreaKey = EAreaPrefix.PartyPrefix | EAreaPrefix.ChatPrefix | EAreaPrefix.LobbyPrefix;

export type IJoinPartyError = 'party-full' | 'not-found' | 'no-authorization' | 'banned';
export type IParticipantTag =
  | 'host'
  | 'spotlighted'
  | 'audio-muted'
  | 'video-muted'
  | 'banned'
  | 'moderator';

export type TPartyPermissions = { [key in TPermissionKey]: any };

export interface IMessage {
  creatorId: TUserId;
  creatorName: string;
  creatorPhotoUrl?: string;
  createdAt: number;
  text: string;
  scope: TAreaKey | TUserId | null;
  localId: string;
  targetUserDisplayName?: string;
  targetUserAvatar?: string;
}

export interface IPartyForUser {
  key: TPartyKey;
  slug: string;
  displayName: string;
  creator: TUserId;
  stage: IPartyStageForUser;
  features: { [key: string]: string };
  areas: {
    [key: string]: IArea;
  };
  messages: Partial<{ [uid in TMessageId]: IMessage }>;
  password?: string;
}
export type IJoinPartyResponse = IJoinPartyError | IPartyForUser;

export enum StreamType {
  Twitch,
  Youtube,
  Backroom,
  Participant,
  None,
  Screenshare,
}

export type Stream =
  | {
      type: StreamType.Twitch;
      twitchId: string;
    }
  | {
      type: StreamType.Youtube;
      youtubeId: string;
      playbackTimestamp?: string;
    }
  | {
      type: StreamType.Backroom;
      backroomStreamId: string;
      audioSource: string;
    }
  | {
      type: StreamType.Participant;
      mainParticipantId: TUserId;
    }
  | {
      type: StreamType.Screenshare;
      mainParticipantId: TUserId;
    }
  | {
      type: StreamType.None;
    };

export interface IPartyStageForUser {
  displayName: string;
  stream: Stream;
  streamHistory: Stream[];
  participants: {
    [uid in TUserId]: IParticipantForUser;
  };
  spotlight: Spotlight | null;
  transitionLength: number;
  bots?: number;
  muteAllParticipants: boolean;
  sessionId?: string;
  links: { [key in string]: IPartyStageLink };
  streamKeys: string[];

  // Deprecated
  videoFeed: {
    twitchId?: string;
    streamUrl?: string;
    youtube?: string;
    playbackTimestamp?: number;
  };
  streamType: keyof IPartyStageForUser['videoFeed'];
  // End deprecated
}

export type TModeratorActionType =
  | 'ban'
  | 'mute-video'
  | 'mute-audio'
  | 'spotlight'
  | 'make-host'
  | 'remove-from-room'
  | 'make-moderator';
export type TAvailableActions = TModeratorActionType;
export type TModeratorActions =
  | {
      type: 'ban';
      value: boolean;
    }
  | {
      type: 'remove-from-room';
      value: boolean;
    }
  | {
      type: 'mute-video';
      value: boolean;
    }
  | {
      type: 'mute-audio';
      value: boolean;
    }
  | {
      type: 'make-host';
      value: boolean;
    }
  | {
      type: 'make-moderator';
      value: boolean;
    };

export interface IParticipantForUser {
  uid: TUserId;

  displayName: string;
  photoURL: string;

  currentArea: TAreaKey;
  tags: IParticipantTag[];

  fakeVideoUrl?: string;
  sessionToken?: string;
  availableActions?: TAvailableActions[];
  roles?: TParticipantRole[];
  active: boolean;
}

export type TParticipantInviteForUserMap = {
  [id in string]: IParticipantInvite;
};

export type IParticipantInviteState = 'invited' | 'invited-seen' | 'accepted' | 'declined';

export interface IParticipantInvite {
  inviterName: string;
  inviterId: TUserId;
  inviteeId: TUserId;
  area: TAreaKey;
  createdAt: number;
  state: IParticipantInviteState;
}
